import {
  JournalEntry,
  WearableMetric,
} from '../../model/dto/journal-entry/journal-entry';
import Colours from '../../../design-system/colours';

export enum WearableDataProvider {
  Oura = 'oura',
  AppleHealth = 'apple_health',
  GoogleHealth = 'google_health',
}

interface WearableProviderConfig {
  displayName: string;
  logoSrc?: string;
}

const WEARABLE_PROVIDER_CONFIG: Record<
  WearableDataProvider,
  WearableProviderConfig
> = {
  [WearableDataProvider.Oura]: {
    displayName: 'Oura',
    logoSrc: '/images/oura/oura-logo-mark-black.png',
  },
  [WearableDataProvider.AppleHealth]: {
    displayName: 'Apple Health',
    logoSrc: '/images/apple-health/apple-health.png',
  },
  [WearableDataProvider.GoogleHealth]: {
    displayName: 'Google Health',
    logoSrc: '/images/google-health/google_health.webp',
  },
};

export enum WearableDataMetricKey {
  TotalSleepDuration = 'total_sleep_duration',
  SleepScore = 'sleep_score',
  ReadinessScore = 'readiness_score',
}

export type WearableScoreInterpretation =
  | 'excellent'
  | 'good'
  | 'fair'
  | 'poor';

export class WearableDataUtils {
  static getProviderDisplayName(provider?: string): string {
    if (!provider) {
      return '';
    }

    const config = WEARABLE_PROVIDER_CONFIG[provider as WearableDataProvider];

    if (config) {
      return config.displayName;
    }

    return provider
      .split(/[_\-\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  static getProviderLogoSrc(provider?: string): string | undefined {
    if (!provider) {
      return undefined;
    }

    return WEARABLE_PROVIDER_CONFIG[provider as WearableDataProvider]?.logoSrc;
  }

  static isAutofilledByProvider(
    journalEntry: JournalEntry | undefined,
    fieldName: string,
    provider?: string
  ): boolean {
    const source = journalEntry?.autofill_sources?.[fieldName];

    if (!source) {
      return false;
    }

    return provider ? source === provider : true;
  }

  static getAutofillProvider(
    journalEntry: JournalEntry | undefined,
    fieldName: string
  ): string | undefined {
    return journalEntry?.autofill_sources?.[fieldName];
  }

  static getWearableMetric(
    journalEntry: JournalEntry | undefined,
    key: WearableDataMetricKey | string,
    provider?: WearableDataProvider | string
  ): WearableMetric | undefined {
    return journalEntry?.wearable_data?.find((metric) => {
      return metric.key === key && (!provider || metric.provider === provider);
    });
  }

  static getKnownWearableMetrics(
    journalEntry: JournalEntry | undefined,
    provider?: WearableDataProvider | string
  ): WearableMetric[] {
    const knownMetricKeys = Object.values(WearableDataMetricKey);

    return (
      journalEntry?.wearable_data?.filter((metric) => {
        return (
          knownMetricKeys.includes(metric.key as WearableDataMetricKey) &&
          (!provider || metric.provider === provider)
        );
      }) ?? []
    );
  }

  /**
   * Returns the provider shared by every given metric, or undefined when they
   * come from different providers and no single attribution applies. Callers
   * should pass only the metrics they actually render.
   */
  static getSharedProvider(
    metrics: (WearableMetric | undefined)[]
  ): string | undefined {
    const providers = metrics
      .filter((metric): metric is WearableMetric => !!metric)
      .map((metric) => metric.provider);

    const uniqueProviders = Array.from(new Set(providers));

    return uniqueProviders.length === 1 ? uniqueProviders[0] : undefined;
  }

  static formatSleepDurationSeconds(value: unknown): string {
    const seconds = WearableDataUtils.getNumberValue(value);

    if (seconds === undefined || seconds < 0) {
      return '-';
    }

    const totalMinutes = Math.round(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours <= 0) {
      return `${minutes} min`;
    }

    if (minutes <= 0) {
      return `${hours} hr`;
    }

    return `${hours} hr ${minutes} min`;
  }

  static getScoreInterpretation(
    scoreValue: unknown
  ): WearableScoreInterpretation | undefined {
    const score = WearableDataUtils.getNumberValue(scoreValue);

    if (score === undefined) {
      return undefined;
    }

    if (score >= 85 && score <= 100) {
      return 'excellent';
    }

    if (score >= 70 && score <= 84) {
      return 'good';
    }

    if (score >= 60 && score <= 69) {
      return 'fair';
    }

    if (score < 60) {
      return 'poor';
    }

    return undefined;
  }

  static getScoreInterpretationColor(scoreValue: unknown): string {
    const interpretation = WearableDataUtils.getScoreInterpretation(scoreValue);

    switch (interpretation) {
      case 'excellent':
        return Colours.OV_DARK_GREEN;
      case 'good':
        return Colours.OV_GREEN;
      case 'fair':
        return Colours.LIGHT_GREEN;
      case 'poor':
        return Colours.OV_YELLOW;
      default:
        return Colours.OV_GREEN;
    }
  }

  static formatUnknownWearableMetric(metric: WearableMetric): string {
    return `${WearableDataUtils.getMetricDisplayName(metric.key)}: ${
      metric.value
    }`;
  }

  static getMetricDisplayName(key: string): string {
    return key
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  static getNumberValue(value: unknown): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }

    return undefined;
  }
}
